import React, { Key, useCallback, useEffect, useState } from "react";
import router from "next/router";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ActiveStatus, CollapsiblePanel } from "src/apps/commons/dataDisplays";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllRemyndProducts } from "src/apps/commons/dropdowns/api";
import {
  SimpleSubTable,
  SimpleSubTablePlaceholder
} from "src/apps/commons/form";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { formatDateTime } from "src/utils";
import { IRemyndProduct } from "../../remyndProduct/types";
import * as Api from "../api";
import {
  IRemyndWarrantyContainer,
  IRemyndWarrantyMatchingSearch
} from "../types";
import { paths } from "../utils";

export type RemyndWarrantyListState = {
  list: IRemyndWarrantyContainer[];
  fetching: boolean;
};

const parseBooleanRadio = (input: string) =>
  input === "true" ? true : input === "false" ? false : undefined;
const formatBooleanRadio = (input?: boolean) =>
  input === undefined ? "" : input.toString();

const SearchWarranty: React.FC<{
  refreshDataKey?: Key;
}> = props => {
  const [product, setProduct] =
    useState<IDropdown<IRemyndProduct> | null>(null);
  const { handleError } = useHttpErrorHandler();

  const [remyndWarrantyListState, setRemyndWarrantyListState] =
    useState<RemyndWarrantyListState>({
      list: [],
      fetching: false
    });

  const fetchRemyndWarranty = useCallback(
    async (search: IRemyndWarrantyMatchingSearch) => {
      setRemyndWarrantyListState({ list: [], fetching: true });
      try {
        const response = await Api.listMatching(search);
        setRemyndWarrantyListState({ list: response.value, fetching: false });
      } catch (e) {
        handleError(e);
        setRemyndWarrantyListState({ list: [], fetching: false });
      }
    },
    [handleError]
  );

  const editWarranty = useCallback((data: IRemyndWarrantyContainer) => {
    const link = `${paths.edit}?id=${data.warranty.id}`;
    router.push(link);
  }, []);

  const [activeState, setActiveState] = useState<boolean | undefined>();
  const handleChangeActive = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    setActiveState(parseBooleanRadio(value));
  };

  useEffect(() => {
    const searchProduct = product?.meta;
    if (searchProduct != null) {
      fetchRemyndWarranty({
        remyndProductId: searchProduct.id,
        remyndBrandId: searchProduct.brand?.id,
        remyndCategoryLv1Id: searchProduct.categoryLv1?.id,
        remyndCategoryLv2Id: searchProduct.categoryLv2?.id,
        remyndCategoryLv3Id: searchProduct.categoryLv3?.id,
        active: activeState
      });
    } else {
      setRemyndWarrantyListState({ list: [], fetching: false });
    }
  }, [activeState, fetchRemyndWarranty, product, props.refreshDataKey]);

  const ActiveCell = useCallback(
    (data: IRemyndWarrantyContainer) => (
      <ActiveStatus value={data.warranty.active} />
    ),
    []
  );

  const MatchingCell = useCallback(
    (data: IRemyndWarrantyContainer) => {
      const p = product?.meta;
      if (p == null) return "";

      const isMatchByProduct = data.productMaps.some(
        d => d.product.id === p.id
      );
      if (isMatchByProduct) return "Matched at Product level.";

      const isMatchByCat3 = data.brandMaps.some(
        d => d.categoryLv3 != null && d.categoryLv3.id === p.categoryLv3?.id
      );
      if (isMatchByCat3) return "Matched at Category level 3.";

      const isMatchByCat2 = data.brandMaps.some(
        d => d.categoryLv2 != null && d.categoryLv2.id === p.categoryLv2?.id
      );
      if (isMatchByCat2) return "Matched at Category level 2.";

      const isMatchByCat1 = data.brandMaps.some(
        d => d.categoryLv1 != null && d.categoryLv1.id === p.categoryLv1?.id
      );
      if (isMatchByCat1) return "Matched at Category level 1.";

      return "Matched at Brand level.";
    },
    [product]
  );

  return (
    <CollapsiblePanel
      title="ค้นหาข้อมูลการรับประกันจากสินค้า"
      icon="search"
      defaultCollapse
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <AsyncDropdown
            label={"ค้นหาจากสินค้า"}
            fetchDropdown={listAllRemyndProducts}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            value={product}
            onChange={setProduct}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Box ml={1}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="position"
                name="active"
                value={formatBooleanRadio(activeState)}
                onChange={handleChangeActive}
              >
                <FormControlLabel
                  value=""
                  control={<Radio color="primary" />}
                  label="ทั้งหมด"
                />
                <FormControlLabel
                  value="true"
                  control={<Radio color="primary" />}
                  label="Active"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio color="primary" />}
                  label="Inactive"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Grid>
        {product && !remyndWarrantyListState.fetching && (
          <Grid item xs={12}>
            <Alert severity="info">
              {"พบ "}
              {remyndWarrantyListState.list.length}
              {" การรับประกันสำหรับสินค้า "}
              {product.meta.nameTh}
              {" (แบรนด์: "}
              {product.meta.brand?.nameTh ?? "ไม่ระบุ"}
              {", หมวดหมู่: "}
              {product.meta.categoryLv1?.nameTh ?? "ไม่ระบุ"}
              {" > "}
              {product.meta.categoryLv2?.nameTh ?? "ไม่ระบุ"}
              {" > "}
              {product.meta.categoryLv3?.nameTh ?? "ไม่ระบุ"}
              {")"}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          {remyndWarrantyListState.fetching ? (
            <SimpleSubTablePlaceholder loading />
          ) : (
            <SimpleSubTable<IRemyndWarrantyContainer>
              data={remyndWarrantyListState.list}
              onClickEdit={editWarranty}
              columns={[
                {
                  label: "ชื่อ (TH)",
                  renderCell: req => req.warranty.titleTh
                },
                {
                  label: "ชื่อ (EN)",
                  renderCell: req => req.warranty.titleEn
                },
                {
                  label: "Matching",
                  align: "center",
                  renderCell: MatchingCell
                },
                {
                  label: "Active",
                  width: 80,
                  align: "center",
                  renderCell: ActiveCell
                },
                {
                  label: "สร้างเมื่อ",
                  width: 180,
                  align: "center",
                  renderCell: req => formatDateTime(req.warranty.createdAt)
                },
                {
                  label: "แก้ไขล่าสุดเมื่อ",
                  width: 180,
                  align: "center",
                  renderCell: req => formatDateTime(req.warranty.updatedAt)
                }
              ]}
              showIndex
            />
          )}
        </Grid>
      </Grid>
    </CollapsiblePanel>
  );
};

export default SearchWarranty;
